import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useSite } from "@/lib/site-context";
import { Phone, Mail, MapPin, Clock, MessageCircle } from "lucide-react";

export default function Contact() {
  const { site } = useSite();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent! We'll be in touch soon.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send. Try again.");
    } finally { setBusy(false); }
  };

  const wa = site?.whatsapp?.replace(/\D/g, "") || "";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
      <div className="text-[11px] uppercase tracking-[0.25em] text-[#D9A62E] font-mono-piki font-semibold">Contact</div>
      <h1 className="mt-3 font-display text-4xl sm:text-5xl font-extrabold text-white">Get in touch</h1>
      <p className="mt-3 text-[#A3A3A3] max-w-xl">Questions, custom quotes, group bookings — we&apos;re here to help.</p>

      <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-10">
        <div className="space-y-4">
          {[
            { icon: Phone, label: "Phone", value: site?.phone, href: `tel:${site?.phone?.replace(/\s/g, "")}` },
            { icon: MessageCircle, label: "WhatsApp", value: site?.whatsapp, href: `https://wa.me/${wa}` },
            { icon: Mail, label: "Email", value: site?.email, href: `mailto:${site?.email}` },
            { icon: MapPin, label: "Address", value: site?.address },
            { icon: Clock, label: "Hours", value: site?.opening_hours },
          ].map((c, i) => (
            <a key={i} href={c.href} className="piki-card p-5 flex items-start gap-4 hover:border-[#D9A62E]" data-testid={`contact-info-${c.label.toLowerCase()}`}>
              <div className="w-10 h-10 rounded-lg bg-[#D9A62E]/10 border border-[#D9A62E]/30 flex items-center justify-center shrink-0">
                <c.icon className="w-4 h-4 text-[#D9A62E]" />
              </div>
              <div>
                <div className="text-xs text-[#737373] uppercase tracking-wider font-mono-piki">{c.label}</div>
                <div className="text-sm text-white font-medium mt-1">{c.value || "—"}</div>
              </div>
            </a>
          ))}
          <a href={`https://wa.me/${wa}`} className="mt-2 piki-gold-btn w-full text-center block" data-testid="contact-whatsapp-quick-link">Message us on WhatsApp</a>
        </div>

        <form onSubmit={submit} className="piki-card p-6 sm:p-8 space-y-4">
          <div>
            <label className="text-xs text-[#A3A3A3] mb-1.5 block">Name</label>
            <input className="piki-input text-sm" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-form-name-input" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-[#A3A3A3] mb-1.5 block">Email</label>
              <input type="email" className="piki-input text-sm" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-form-email-input" />
            </div>
            <div>
              <label className="text-xs text-[#A3A3A3] mb-1.5 block">Phone</label>
              <input className="piki-input text-sm" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="contact-form-phone-input" />
            </div>
          </div>
          <div>
            <label className="text-xs text-[#A3A3A3] mb-1.5 block">Message</label>
            <textarea rows={5} className="piki-input text-sm" required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-form-message-input" />
          </div>
          <button type="submit" disabled={busy} className="piki-gold-btn w-full disabled:opacity-40" data-testid="contact-form-submit-btn">
            {busy ? "Sending…" : "Send Message"}
          </button>
        </form>
      </div>
    </div>
  );
}
